import React, { useState } from 'react'
import styled from 'styled-components'
import { FieldArray } from 'react-final-form-arrays'
import arrayMutators from 'final-form-arrays'
import { Field } from 'react-final-form'

import { useRequest } from '../hooks/request'
import {
  notificationSettingGet, notificationSettingUpdate,
  notificationSettingUpdatePath,
} from '../requests/notificationSetting'
import { NOTIFICATION_SETTINGS_TYPE } from '../constants/notificationSettingType'
import { theme } from '../constants/theme'

import { PageTitle } from './common/PageTitle'
import { PageContent } from './layout/layout'
import { Loader } from './icons/Loader'
import { CenterStuff } from './common/CenterStuff'
import { Trad } from './common/Trad'
import { Pen } from './icons/Pen'
import {
  BlueButton, GreenButton, OrangeButton, YellowButton,
} from './common/Button'
import { Bin } from './icons/Bin'
import { AddPeople } from './icons/AddPeople'
import { Margin } from './common/Margin'
import { Form } from './common/Form'
import { Save } from './icons/Save'
import { Block } from './common/Block'
import { InputField } from './fields/InputField'


// specific order
const settingsOrder = [
  NOTIFICATION_SETTINGS_TYPE.facmReschedule,
  NOTIFICATION_SETTINGS_TYPE.facmStart,
  NOTIFICATION_SETTINGS_TYPE.facmEnd,
  NOTIFICATION_SETTINGS_TYPE.rescueGDH,
  NOTIFICATION_SETTINGS_TYPE.dailyRaces,
]


export const NotificationSettingShow = () => {
  const [editing, setEditing] = useState()
  const notificationSettingRequest = useRequest({
    request: notificationSettingGet,
    start: true,
  })
  const notificationSettingUpdateRequest = useRequest({
    request: notificationSettingUpdate,
    handler: async (p) => {
      const res = await p
      notificationSettingRequest.setData({ data: res })
    },
  })


  if (notificationSettingRequest.state.loading || !notificationSettingRequest.state.data) {
    return (
      <CenterStuff>
        <Loader height={100} />
      </CenterStuff>
    )
  }

  const notificationSetting = notificationSettingUpdateRequest.state.data || notificationSettingRequest.state.data

  return (
    <Form
      onSubmit={notificationSettingUpdateRequest.fetch}
      mutators={{ ...arrayMutators }}
      path={notificationSettingUpdatePath}
      initialValues={notificationSetting}
    >
      {({ handleSubmit, form }) => {
        const state = form.getState()

        return (
          <>
            <PageTitle tradId="notificationSettings" />
            <PageContent>
              <SettingHelper>
                <Trad id="settingExplanation" />
              </SettingHelper>
              {settingsOrder.map((settingName) => (
                <SettingWrapper key={settingName}>
                  <FieldArray name={settingName}>
                    {({ fields }) => (
                      <>
                        <SettingContent>
                          <SettingTitle>
                            <Trad id={settingName} />
                          </SettingTitle>
                          {fields.map((fieldName, i) => {
                            const id = settingName + i
                            const currentEdit = editing === id

                            return (
                              <Row key={settingName + i}>
                                <NameWrapper>
                                  <Field
                                    disabled={!currentEdit}
                                    name={`${fieldName}.lastName`}
                                    placeholder="lastnamePlaceholder"
                                    component={CustomInputField}
                                  />
                                </NameWrapper>
                                <Margin h={10} />
                                <NameWrapper>
                                  <Field
                                    disabled={!currentEdit}
                                    name={`${fieldName}.firstName`}
                                    placeholder="firstnamePlaceholder"
                                    component={CustomInputField}
                                  />
                                </NameWrapper>
                                <Margin h={10} />
                                <EmailWrapper>
                                  <Field
                                    disabled={!currentEdit}
                                    name={`${fieldName}.email`}
                                    placeholder="emailPlaceholder"
                                    component={CustomInputField}
                                  />
                                </EmailWrapper>
                                <ControlWrapper>
                                  <Margin h={20} />
                                  {currentEdit ? (
                                    <GreenButton
                                      icon={<Save />}
                                      loading={notificationSettingUpdateRequest.state.loading}
                                      onClick={async () => {
                                        const errors = await handleSubmit()
                                        if (state.valid) {
                                          if (!errors) {
                                            setEditing()
                                          }
                                        }
                                      }}
                                    />
                                  ) : (
                                    <BlueButton
                                      icon={<Pen />}
                                      onClick={() => {
                                        if (state.valid) {
                                          setEditing(id)
                                        } else {
                                          handleSubmit()
                                        }
                                      }}
                                    />
                                  )}
                                  <Margin h={20} />
                                  <OrangeButton
                                    loading={notificationSettingUpdateRequest.state.loading}
                                    icon={<Bin />}
                                    onClick={async () => {
                                      const oldValues = notificationSetting[settingName][i]
                                      fields.remove(i)
                                      const errors = await handleSubmit()
                                      if (errors) {
                                        fields.insert(settingName, i, oldValues)
                                      }
                                    }}
                                  />
                                  <Margin h={20} />
                                </ControlWrapper>
                              </Row>
                            )
                          })}
                        </SettingContent>
                        <AddWrapper>
                          <YellowButton
                            icon={<AddPeople />}
                            loading={notificationSettingUpdateRequest.state.loading}
                            onClick={async () => {
                              if (state.valid) {
                                if (editing) {
                                  await handleSubmit()
                                }
                                fields.push({})
                                setEditing(settingName + fields.length)
                              } else {
                                handleSubmit()
                              }
                            }}
                          />
                          <Margin h={10} />
                          <Trad id="addEmail" />
                        </AddWrapper>
                      </>
                    )}
                  </FieldArray>
                </SettingWrapper>
              ))}
            </PageContent>
          </>
        )
      }}
    </Form>
  )
}

const CustomInputField = styled(InputField)`
  margin: 0;
`
const ControlWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  @media (max-width: 1000px) {
    flex-direction: column;
  }
`

const SettingHelper = styled.div`
  padding-bottom: 34px;
  font-size: 22px;
`
const SettingWrapper = styled.div`
  padding: 18px 0;
`

const NameWrapper = styled.div`
  width: 165px;
`
const EmailWrapper = styled.div`
  flex: 1;
`

const SettingTitle = styled.div`
  color: ${theme.colors.blueCommon};
  font-size: 22px;
  padding-bottom: 26px;
  font-weight: 600px;
`
const SettingContent = styled.div`
  padding: 0 20px;
`
const AddWrapper = styled.div`
  display: flex;
  align-items: center;
  padding-top: 17px;
`
const Row = styled(Block)`
  padding: 10px 10px;;
  display: flex;
  flex-direction: row;
  overflow-wrap: anywhere;
  &>* {
    flex: 1;
  }
`
